/**
 * Username + badge — the ONE client-rendered badge component (T212588).
 *
 * Its Razor twin is Views/Partials/Components/UsernameWithBadge.cshtml and emits identical
 * markup; both are styled by assets/src/css/modules/components/user-badge.scss. If you change
 * the DOM here, change it there too.
 *
 * There is deliberately no tippy code in this file. Badge tooltips are handled by one
 * delegated instance registered in js/components/badge-tooltip.js (loaded by common.js), which
 * targets `[data-badge-tooltip]`. React can therefore mount, unmount and re-render badges
 * freely, and the tooltip behaviour cannot diverge from the server-rendered one.
 *
 * Accessibility: the visually-hidden suffix sits INSIDE the username element so its accessible
 * name computes to "karmacat, Valspar Influencer" as a single announcement. The trigger carries
 * its own short label instead of repeating the handle.
 *
 * `badge` shape (matches MemberBadgeViewModel on the server):
 *   { variant, label, colour, tooltipHtml, ariaLabel }
 * A null/undefined `badge` is a supported state — the component collapses to a plain username.
 */

const MODE_CLASS = {
  icon: "user-badge--icon",
  pill: "user-badge--pill",
};

export const UsernameWithBadge = ({
  username,
  profileUrl,
  badge,
  mode = "icon",
  className = "",
  nameClassName = "",
  prefixWithAt = false,
  truncate = false,
}) => {
  const name = (username ?? "").trim();

  if (!name) {
    return null;
  }

  const displayName = prefixWithAt && !name.startsWith("@") ? `@${name}` : name;

  const wrapperClassName = [
    "user-badge",
    MODE_CLASS[mode] ?? MODE_CLASS.icon,
    truncate ? "user-badge--truncate" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const srSuffix = badge?.ariaLabel ? `, ${badge.ariaLabel}` : null;

  const nameContent = (
    <>
      {displayName}
      {srSuffix && <span className="user-badge__sr">{srSuffix}</span>}
    </>
  );

  return (
    <span className={wrapperClassName} data-testid="username-with-badge">
      {profileUrl ? (
        <a
          className={`user-badge__name ${nameClassName}`.trim()}
          href={profileUrl}
          data-testid="username-with-badge-name"
        >
          {nameContent}
        </a>
      ) : (
        <span
          className={`user-badge__name ${nameClassName}`.trim()}
          data-testid="username-with-badge-name"
        >
          {nameContent}
        </span>
      )}
      {badge && (
        <button
          type="button"
          className="user-badge__trigger"
          data-testid="influencer-badge"
          data-badge-variant={badge.variant}
          data-badge-tooltip={badge.tooltipHtml}
          aria-label={`About the ${badge.label} badge`}
          aria-expanded="false"
          style={{ "--user-badge-colour": badge.colour }}
        >
          <svg className="user-badge__icon" aria-hidden="true" focusable="false">
            <use href="#icon-badge-rosette" />
          </svg>
          <span className="user-badge__label">{badge.label}</span>
        </button>
      )}
    </span>
  );
};
